// src/modules/Admin/pages/Configuracoes/index.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { supabase } from '../../../../config/supabase';
import { useAuth } from '../../../../contexts/AuthContext';
import {
  FaGlobe,
  FaNewspaper,
  FaUsers,
  FaSearch,
  FaUserShield,
  FaSave,
  FaPlus,
  FaTrash,
  FaCheck,
  FaTimes,
  FaSpinner,
  FaPhone,
  FaEnvelope,
  FaWhatsapp,
  FaMapMarkerAlt,
  FaLinkedin,
  FaInstagram,
  FaYoutube,
  FaExternalLinkAlt,
  FaDatabase,
  FaGoogle,
  FaRobot,
  FaKey,
  FaCloud,
  FaBook,
  FaCog,
  FaInfoCircle,
  FaClipboard,
  FaEdit
} from 'react-icons/fa';

// ===== STYLED COMPONENTS =====
const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  margin-bottom: 32px;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #111;
  margin-bottom: 8px;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: #666;
`;

const TabsContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
  flex-wrap: wrap;
  background: white;
  padding: 8px;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const Tab = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: ${props => props.$active ? '#111' : 'transparent'};
  color: ${props => props.$active ? 'white' : '#666'};
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 200ms;

  &:hover {
    background: ${props => props.$active ? '#111' : '#f5f5f5'};
    color: ${props => props.$active ? 'white' : '#333'};
  }

  svg {
    font-size: 16px;
  }
`;

const Section = styled.section`
  background: white;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #eee;
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  display: flex;
  align-items: center;
  gap: 10px;

  svg {
    color: #666;
  }
`;

const SaveButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #111;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 200ms;

  &:hover:not(:disabled) {
    background: #333;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  svg {
    font-size: 14px;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: #333;
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    color: #666;
    font-size: 14px;
  }
`;

const Input = styled.input`
  padding: 12px 14px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  color: #333;
  transition: all 200ms;

  &:focus {
    outline: none;
    border-color: #111;
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.1);
  }

  &::placeholder {
    color: #999;
  }
`;

const TextArea = styled.textarea`
  padding: 12px 14px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  color: #333;
  min-height: 100px;
  resize: vertical;
  font-family: inherit;
  transition: all 200ms;

  &:focus {
    outline: none;
    border-color: #111;
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.1);
  }
`;

const Select = styled.select`
  padding: 12px 14px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  color: #333;
  background: white;
  cursor: pointer;
  transition: all 200ms;

  &:focus {
    outline: none;
    border-color: #111;
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.1);
  }
`;

const Toggle = styled.label`
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  padding: 12px 0;

  input {
    display: none;
  }
`;

const ToggleSwitch = styled.span`
  position: relative;
  width: 48px;
  height: 26px;
  background: ${props => props.$checked ? '#10b981' : '#e0e0e0'};
  border-radius: 13px;
  transition: background 200ms;

  &::after {
    content: '';
    position: absolute;
    top: 3px;
    left: ${props => props.$checked ? '25px' : '3px'};
    width: 20px;
    height: 20px;
    background: white;
    border-radius: 50%;
    transition: left 200ms;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }
`;

const ToggleLabel = styled.span`
  font-size: 14px;
  color: #333;
`;

const TagsInput = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  min-height: 48px;
  background: white;

  &:focus-within {
    border-color: #111;
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.1);
  }
`;

const Tag = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: #f0f0f0;
  border-radius: 6px;
  font-size: 13px;
  color: #333;

  button {
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    color: #999;
    cursor: pointer;
    padding: 0;

    &:hover {
      color: #ef4444;
    }
  }
`;

const TagInput = styled.input`
  flex: 1;
  min-width: 120px;
  border: none;
  outline: none;
  font-size: 14px;
  padding: 4px;
`;

const AdminList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const AdminItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 10px;
  transition: all 200ms;

  &:hover {
    background: #f0f0f0;
  }
`;

const AdminInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const AdminAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #111;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
`;

const AdminDetails = styled.div`
  .name {
    font-size: 14px;
    font-weight: 600;
    color: #333;
  }
  .email {
    font-size: 13px;
    color: #666;
  }
`;

const DeleteButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: none;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  color: #999;
  cursor: pointer;
  transition: all 200ms;

  &:hover {
    background: #fef2f2;
    border-color: #ef4444;
    color: #ef4444;
  }
`;

const AddAdminForm = styled.form`
  display: flex;
  gap: 12px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #eee;
`;

const AddInput = styled.input`
  flex: 1;
  padding: 12px 14px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #111;
  }
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: #10b981;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 200ms;

  &:hover:not(:disabled) {
    background: #059669;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Message = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 18px;
  border-radius: 10px;
  font-size: 14px;
  margin-bottom: 20px;
  background: ${props => props.$type === 'success' ? '#ecfdf5' : '#fef2f2'};
  color: ${props => props.$type === 'success' ? '#065f46' : '#991b1b'};
  border: 1px solid ${props => props.$type === 'success' ? '#a7f3d0' : '#fecaca'};

  svg {
    font-size: 18px;
  }
`;

const LoadingOverlay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px;
  color: #666;

  svg {
    animation: spin 1s linear infinite;
    margin-right: 10px;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const LinksGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
`;

const LinkCard = styled.a`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 10px;
  text-decoration: none;
  transition: all 200ms;
  border: 1px solid transparent;

  &:hover {
    background: #fff;
    border-color: #e0e0e0;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const IconWrapper = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 10px;
  background: ${props => props.$bg || '#111'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    font-size: 20px;
    color: white;
  }
`;

const LinkContent = styled.div`
  flex: 1;
`;

const LinkTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #111;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    font-size: 12px;
    color: #999;
  }
`;

const LinkDescription = styled.div`
  font-size: 13px;
  color: #666;
  line-height: 1.5;
`;

const InfoBox = styled.div`
  background: linear-gradient(135deg, #f0f7ff 0%, #e8f4ff 100%);
  border: 1px solid #cce5ff;
  border-radius: 10px;
  padding: 16px 20px;
  margin-bottom: 20px;
  display: flex;
  align-items: flex-start;
  gap: 12px;

  svg {
    color: #0056b3;
    font-size: 18px;
    margin-top: 2px;
  }
`;

const InfoText = styled.div`
  font-size: 13px;
  color: #004085;
  line-height: 1.6;
`;

// ===== COMPONENTE PRINCIPAL =====
const Configuracoes = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('site');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  // Estados das configurações
  const [settings, setSettings] = useState({});
  const [admins, setAdmins] = useState([]);
  const [newAdmin, setNewAdmin] = useState({ email: '', name: '' });
  const [newKeyword, setNewKeyword] = useState('');

  // Estados para templates
  const [templates, setTemplates] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [newTemplate, setNewTemplate] = useState({ name: '', category: 'primeiro_contato', content: '' });

  // Carregar dados
  useEffect(() => {
    fetchSettings();
    fetchAdmins();
    fetchTemplates();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('settings')
        .select('*');

      if (error) throw error;

      // Converter array para objeto
      const settingsObj = {};
      data?.forEach(item => {
        settingsObj[item.key] = item.value;
      });
      setSettings(settingsObj);
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
      setMessage({ type: 'error', text: 'Erro ao carregar configurações. Verifique se a tabela existe.' });
    } finally {
      setLoading(false);
    }
  };

  const fetchAdmins = async () => {
    try {
      const { data, error } = await supabase
        .from('admin_users')
        .select('*')
        .order('created_at', { ascending: true });

      if (error) throw error;
      setAdmins(data || []);
    } catch (error) {
      console.error('Erro ao carregar admins:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const { data, error } = await supabase
        .from('message_templates')
        .select('*')
        .order('category', { ascending: true });

      if (error) throw error;
      setTemplates(data || []);
    } catch (error) {
      console.error('Erro ao carregar templates:', error);
    }
  };

  const addTemplate = async () => {
    if (!newTemplate.name.trim() || !newTemplate.content.trim()) {
      setMessage({ type: 'error', text: 'Preencha todos os campos do template' });
      return;
    }

    setSaving(true);
    try {
      const { data, error } = await supabase
        .from('message_templates')
        .insert({
          name: newTemplate.name.trim(),
          category: newTemplate.category,
          content: newTemplate.content.trim(),
          created_by: user?.email
        })
        .select()
        .single();

      if (error) throw error;

      setTemplates(prev => [...prev, data]);
      setNewTemplate({ name: '', category: 'primeiro_contato', content: '' });
      setMessage({ type: 'success', text: 'Template adicionado com sucesso!' });
    } catch (error) {
      console.error('Erro ao adicionar template:', error);
      setMessage({ type: 'error', text: 'Erro ao adicionar template' });
    } finally {
      setSaving(false);
    }
  };

  const updateTemplate = async (template) => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('message_templates')
        .update({
          name: template.name,
          category: template.category,
          content: template.content,
          updated_at: new Date().toISOString()
        })
        .eq('id', template.id);

      if (error) throw error;

      setTemplates(prev => prev.map(t => t.id === template.id ? template : t));
      setEditingTemplate(null);
      setMessage({ type: 'success', text: 'Template atualizado com sucesso!' });
    } catch (error) {
      console.error('Erro ao atualizar template:', error);
      setMessage({ type: 'error', text: 'Erro ao atualizar template' });
    } finally {
      setSaving(false);
    }
  };

  const deleteTemplate = async (templateId) => {
    if (!window.confirm('Tem certeza que deseja excluir este template?')) return;

    try {
      const { error } = await supabase
        .from('message_templates')
        .delete()
        .eq('id', templateId);

      if (error) throw error;

      setTemplates(prev => prev.filter(t => t.id !== templateId));
      setMessage({ type: 'success', text: 'Template excluído com sucesso!' });
    } catch (error) {
      console.error('Erro ao excluir template:', error);
      setMessage({ type: 'error', text: 'Erro ao excluir template' });
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const saveSettings = async (category) => {
    setSaving(true);
    setMessage(null);

    try {
      // Filtrar settings por categoria
      const categoryKeys = {
        site: ['site_phone', 'site_email', 'site_whatsapp', 'site_address', 'social_linkedin', 'social_instagram', 'social_youtube'],
        blog: ['blog_posts_per_page', 'blog_enable_comments', 'blog_enable_share'],
        leads: ['leads_notification_emails', 'leads_cold_days', 'leads_auto_assign'],
        seo: ['seo_default_title', 'seo_default_description', 'seo_keywords', 'seo_og_image']
      };

      const keysToSave = categoryKeys[category] || [];

      for (const key of keysToSave) {
        if (settings[key] !== undefined) {
          const { error } = await supabase
            .from('settings')
            .upsert({
              key,
              value: settings[key],
              category,
              updated_at: new Date().toISOString()
            }, { onConflict: 'key' });

          if (error) throw error;
        }
      }

      setMessage({ type: 'success', text: 'Configurações salvas com sucesso!' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Erro ao salvar:', error);
      setMessage({ type: 'error', text: 'Erro ao salvar configurações.' });
    } finally {
      setSaving(false);
    }
  };

  const addAdmin = async (e) => {
    e.preventDefault();
    if (!newAdmin.email) return;

    try {
      const { error } = await supabase
        .from('admin_users')
        .insert({
          email: newAdmin.email.toLowerCase(),
          name: newAdmin.name || null,
          added_by: user?.email
        });

      if (error) throw error;

      setNewAdmin({ email: '', name: '' });
      fetchAdmins();
      setMessage({ type: 'success', text: 'Admin adicionado com sucesso!' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Erro ao adicionar admin:', error);
      setMessage({ type: 'error', text: error.message.includes('duplicate') ? 'Este email já é admin.' : 'Erro ao adicionar admin.' });
    }
  };

  const removeAdmin = async (id, email) => {
    if (email === user?.email) {
      setMessage({ type: 'error', text: 'Você não pode remover a si mesmo!' });
      return;
    }

    if (!window.confirm(`Remover ${email} da lista de admins?`)) return;

    try {
      const { error } = await supabase
        .from('admin_users')
        .delete()
        .eq('id', id);

      if (error) throw error;

      fetchAdmins();
      setMessage({ type: 'success', text: 'Admin removido com sucesso!' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Erro ao remover admin:', error);
      setMessage({ type: 'error', text: 'Erro ao remover admin.' });
    }
  };

  const addKeyword = (e) => {
    if (e.key === 'Enter' && newKeyword.trim()) {
      e.preventDefault();
      const currentKeywords = settings.seo_keywords || [];
      if (!currentKeywords.includes(newKeyword.trim())) {
        updateSetting('seo_keywords', [...currentKeywords, newKeyword.trim()]);
      }
      setNewKeyword('');
    }
  };

  const removeKeyword = (keyword) => {
    const currentKeywords = settings.seo_keywords || [];
    updateSetting('seo_keywords', currentKeywords.filter(k => k !== keyword));
  };

  const getInitials = (name, email) => {
    if (name) return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    return email?.slice(0, 2).toUpperCase() || '??';
  };

  // Links úteis
  const usefulLinks = [
    { title: 'Supabase Dashboard', description: 'Banco de dados, auth e storage', url: 'https://supabase.com/dashboard/project/rotohdtbuqpeymjaiybu', icon: FaDatabase, bg: '#3ECF8E' },
    { title: 'Google Cloud Console', description: 'OAuth e credenciais', url: 'https://console.cloud.google.com/apis/credentials', icon: FaGoogle, bg: '#4285F4' },
    { title: 'OpenAI Platform', description: 'API e monitoramento', url: 'https://platform.openai.com/usage', icon: FaRobot, bg: '#10A37F' },
    { title: 'Supabase Storage', description: 'Buckets e arquivos', url: 'https://supabase.com/dashboard/project/rotohdtbuqpeymjaiybu/storage/buckets', icon: FaCloud, bg: '#F59E0B' },
  ];

  if (loading) {
    return (
      <Container>
        <LoadingOverlay>
          <FaSpinner /> Carregando configurações...
        </LoadingOverlay>
      </Container>
    );
  }

  return (
    <Container>
      <PageHeader>
        <Title>Configurações</Title>
        <Subtitle>Gerencie as configurações do site, blog, leads e SEO</Subtitle>
      </PageHeader>

      {message && (
        <Message $type={message.type}>
          {message.type === 'success' ? <FaCheck /> : <FaTimes />}
          {message.text}
        </Message>
      )}

      <TabsContainer>
        <Tab $active={activeTab === 'site'} onClick={() => setActiveTab('site')}>
          <FaGlobe /> Site
        </Tab>
        <Tab $active={activeTab === 'blog'} onClick={() => setActiveTab('blog')}>
          <FaNewspaper /> Blog
        </Tab>
        <Tab $active={activeTab === 'leads'} onClick={() => setActiveTab('leads')}>
          <FaUsers /> Leads
        </Tab>
        <Tab $active={activeTab === 'seo'} onClick={() => setActiveTab('seo')}>
          <FaSearch /> SEO
        </Tab>
        <Tab $active={activeTab === 'admins'} onClick={() => setActiveTab('admins')}>
          <FaUserShield /> Admins
        </Tab>
        <Tab $active={activeTab === 'links'} onClick={() => setActiveTab('links')}>
          <FaExternalLinkAlt /> Links Úteis
        </Tab>
      </TabsContainer>

      {/* ABA: SITE */}
      {activeTab === 'site' && (
        <Section>
          <SectionHeader>
            <SectionTitle><FaGlobe /> Configurações do Site</SectionTitle>
            <SaveButton onClick={() => saveSettings('site')} disabled={saving}>
              {saving ? <FaSpinner className="spin" /> : <FaSave />}
              Salvar
            </SaveButton>
          </SectionHeader>

          <FormGrid>
            <FormGroup>
              <Label><FaPhone /> Telefone</Label>
              <Input
                type="text"
                value={settings.site_phone || ''}
                onChange={(e) => updateSetting('site_phone', e.target.value)}
                placeholder="(11) 94427-1000"
              />
            </FormGroup>

            <FormGroup>
              <Label><FaEnvelope /> Email de Contato</Label>
              <Input
                type="email"
                value={settings.site_email || ''}
                onChange={(e) => updateSetting('site_email', e.target.value)}
                placeholder="contato@vegarobotics.com.br"
              />
            </FormGroup>

            <FormGroup>
              <Label><FaWhatsapp /> WhatsApp</Label>
              <Input
                type="text"
                value={settings.site_whatsapp || ''}
                onChange={(e) => updateSetting('site_whatsapp', e.target.value)}
                placeholder="+5511944271000"
              />
            </FormGroup>

            <FormGroup>
              <Label><FaMapMarkerAlt /> Endereço</Label>
              <Input
                type="text"
                value={settings.site_address || ''}
                onChange={(e) => updateSetting('site_address', e.target.value)}
                placeholder="São Paulo, SP - Brasil"
              />
            </FormGroup>

            <FormGroup>
              <Label><FaLinkedin /> LinkedIn</Label>
              <Input
                type="url"
                value={settings.social_linkedin || ''}
                onChange={(e) => updateSetting('social_linkedin', e.target.value)}
                placeholder="https://linkedin.com/company/..."
              />
            </FormGroup>

            <FormGroup>
              <Label><FaInstagram /> Instagram</Label>
              <Input
                type="url"
                value={settings.social_instagram || ''}
                onChange={(e) => updateSetting('social_instagram', e.target.value)}
                placeholder="https://instagram.com/..."
              />
            </FormGroup>

            <FormGroup>
              <Label><FaYoutube /> YouTube</Label>
              <Input
                type="url"
                value={settings.social_youtube || ''}
                onChange={(e) => updateSetting('social_youtube', e.target.value)}
                placeholder="https://youtube.com/..."
              />
            </FormGroup>
          </FormGrid>
        </Section>
      )}

      {/* ABA: BLOG */}
      {activeTab === 'blog' && (
        <Section>
          <SectionHeader>
            <SectionTitle><FaNewspaper /> Configurações do Blog</SectionTitle>
            <SaveButton onClick={() => saveSettings('blog')} disabled={saving}>
              {saving ? <FaSpinner className="spin" /> : <FaSave />}
              Salvar
            </SaveButton>
          </SectionHeader>

          <FormGrid>
            <FormGroup>
              <Label>Posts por página</Label>
              <Input
                type="number"
                min="3"
                max="24"
                value={settings.blog_posts_per_page || 9}
                onChange={(e) => updateSetting('blog_posts_per_page', parseInt(e.target.value))}
              />
            </FormGroup>
          </FormGrid>

          <div style={{ marginTop: '20px' }}>
            <Toggle>
              <input
                type="checkbox"
                checked={settings.blog_enable_comments || false}
                onChange={(e) => updateSetting('blog_enable_comments', e.target.checked)}
              />
              <ToggleSwitch $checked={settings.blog_enable_comments || false} />
              <ToggleLabel>Habilitar comentários nos posts</ToggleLabel>
            </Toggle>

            <Toggle>
              <input
                type="checkbox"
                checked={settings.blog_enable_share !== false}
                onChange={(e) => updateSetting('blog_enable_share', e.target.checked)}
              />
              <ToggleSwitch $checked={settings.blog_enable_share !== false} />
              <ToggleLabel>Habilitar botões de compartilhamento</ToggleLabel>
            </Toggle>
          </div>
        </Section>
      )}

      {/* ABA: LEADS */}
      {activeTab === 'leads' && (
        <Section>
          <SectionHeader>
            <SectionTitle><FaUsers /> Configurações de Leads</SectionTitle>
            <SaveButton onClick={() => saveSettings('leads')} disabled={saving}>
              {saving ? <FaSpinner className="spin" /> : <FaSave />}
              Salvar
            </SaveButton>
          </SectionHeader>

          <InfoBox>
            <FaInfoCircle />
            <InfoText>
              Configure como os leads capturados pelo site devem ser gerenciados.
              Os emails de notificação receberão alertas quando novos leads chegarem.
            </InfoText>
          </InfoBox>

          <FormGrid>
            <FormGroup>
              <Label>Emails para notificação (separados por vírgula)</Label>
              <Input
                type="text"
                value={Array.isArray(settings.leads_notification_emails) ? settings.leads_notification_emails.join(', ') : settings.leads_notification_emails || ''}
                onChange={(e) => updateSetting('leads_notification_emails', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                placeholder="email1@exemplo.com, email2@exemplo.com"
              />
            </FormGroup>

            <FormGroup>
              <Label>Dias para considerar lead "frio"</Label>
              <Input
                type="number"
                min="1"
                max="90"
                value={settings.leads_cold_days || 7}
                onChange={(e) => updateSetting('leads_cold_days', parseInt(e.target.value))}
              />
            </FormGroup>
          </FormGrid>

          <div style={{ marginTop: '20px' }}>
            <Toggle>
              <input
                type="checkbox"
                checked={settings.leads_auto_assign || false}
                onChange={(e) => updateSetting('leads_auto_assign', e.target.checked)}
              />
              <ToggleSwitch $checked={settings.leads_auto_assign || false} />
              <ToggleLabel>Atribuir leads automaticamente aos vendedores</ToggleLabel>
            </Toggle>
          </div>
        </Section>
      )}

      {/* ABA: LEADS - Templates */}
      {activeTab === 'leads' && (
        <Section>
          <SectionHeader>
            <SectionTitle><FaClipboard /> Templates de Mensagem</SectionTitle>
          </SectionHeader>

          <InfoBox>
            <FaInfoCircle />
            <InfoText>
              Crie templates de mensagem para agilizar a comunicação com leads.
              Use variáveis: {'{nome}'}, {'{empresa}'}, {'{email}'}, {'{telefone}'}
            </InfoText>
          </InfoBox>

          {/* Formulário para novo template */}
          <div style={{
            background: '#f9fafb',
            borderRadius: '10px',
            padding: '20px',
            marginBottom: '20px'
          }}>
            <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '16px', color: '#333' }}>
              <FaPlus style={{ marginRight: '8px' }} /> Novo Template
            </h4>
            <FormGrid>
              <FormGroup>
                <Label>Nome do template</Label>
                <Input
                  type="text"
                  value={newTemplate.name}
                  onChange={(e) => setNewTemplate(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Ex: Primeiro contato"
                />
              </FormGroup>
              <FormGroup>
                <Label>Categoria</Label>
                <Select
                  value={newTemplate.category}
                  onChange={(e) => setNewTemplate(prev => ({ ...prev, category: e.target.value }))}
                >
                  <option value="primeiro_contato">Primeiro Contato</option>
                  <option value="follow_up">Follow-up</option>
                  <option value="proposta">Proposta</option>
                  <option value="fechamento">Fechamento</option>
                  <option value="reativacao">Reativação</option>
                </Select>
              </FormGroup>
            </FormGrid>
            <FormGroup style={{ marginTop: '12px' }}>
              <Label>Conteúdo da mensagem</Label>
              <TextArea
                value={newTemplate.content}
                onChange={(e) => setNewTemplate(prev => ({ ...prev, content: e.target.value }))}
                placeholder="Olá {nome}, tudo bem? Sou da Vega Robotics e gostaria de conversar sobre..."
                rows={4}
              />
            </FormGroup>
            <div style={{ marginTop: '12px' }}>
              <SaveButton onClick={addTemplate} disabled={saving || !newTemplate.name || !newTemplate.content}>
                {saving ? <FaSpinner className="spin" /> : <FaPlus />}
                Adicionar Template
              </SaveButton>
            </div>
          </div>

          {/* Lista de templates */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {templates.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                Nenhum template cadastrado ainda
              </div>
            ) : (
              templates.map(template => (
                <div
                  key={template.id}
                  style={{
                    background: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '10px',
                    padding: '16px'
                  }}
                >
                  {editingTemplate?.id === template.id ? (
                    // Modo edição
                    <>
                      <FormGrid>
                        <FormGroup>
                          <Label>Nome</Label>
                          <Input
                            type="text"
                            value={editingTemplate.name}
                            onChange={(e) => setEditingTemplate(prev => ({ ...prev, name: e.target.value }))}
                          />
                        </FormGroup>
                        <FormGroup>
                          <Label>Categoria</Label>
                          <Select
                            value={editingTemplate.category}
                            onChange={(e) => setEditingTemplate(prev => ({ ...prev, category: e.target.value }))}
                          >
                            <option value="primeiro_contato">Primeiro Contato</option>
                            <option value="follow_up">Follow-up</option>
                            <option value="proposta">Proposta</option>
                            <option value="fechamento">Fechamento</option>
                            <option value="reativacao">Reativação</option>
                          </Select>
                        </FormGroup>
                      </FormGrid>
                      <FormGroup style={{ marginTop: '12px' }}>
                        <Label>Conteúdo</Label>
                        <TextArea
                          value={editingTemplate.content}
                          onChange={(e) => setEditingTemplate(prev => ({ ...prev, content: e.target.value }))}
                          rows={4}
                        />
                      </FormGroup>
                      <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                        <SaveButton onClick={() => updateTemplate(editingTemplate)} disabled={saving}>
                          {saving ? <FaSpinner className="spin" /> : <FaCheck />}
                          Salvar
                        </SaveButton>
                        <DeleteButton onClick={() => setEditingTemplate(null)} style={{ background: '#6b7280' }}>
                          <FaTimes /> Cancelar
                        </DeleteButton>
                      </div>
                    </>
                  ) : (
                    // Modo visualização
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                        <div>
                          <span style={{
                            fontSize: '10px',
                            fontWeight: '600',
                            textTransform: 'uppercase',
                            color: '#666',
                            background: '#e5e7eb',
                            padding: '2px 8px',
                            borderRadius: '4px'
                          }}>
                            {template.category.replace('_', ' ')}
                          </span>
                          <h4 style={{ fontSize: '15px', fontWeight: '600', color: '#111', marginTop: '8px' }}>
                            {template.name}
                          </h4>
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => setEditingTemplate(template)}
                            style={{
                              background: '#f3f4f6',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '8px 12px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              fontSize: '12px',
                              color: '#374151'
                            }}
                          >
                            <FaEdit /> Editar
                          </button>
                          <button
                            onClick={() => deleteTemplate(template.id)}
                            style={{
                              background: '#fef2f2',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '8px 12px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              fontSize: '12px',
                              color: '#dc2626'
                            }}
                          >
                            <FaTrash /> Excluir
                          </button>
                        </div>
                      </div>
                      <div style={{
                        background: '#f9fafb',
                        borderRadius: '8px',
                        padding: '12px',
                        fontSize: '13px',
                        color: '#374151',
                        lineHeight: '1.6',
                        whiteSpace: 'pre-wrap'
                      }}>
                        {template.content}
                      </div>
                    </>
                  )}
                </div>
              ))
            )}
          </div>
        </Section>
      )}

      {/* ABA: SEO */}
      {activeTab === 'seo' && (
        <Section>
          <SectionHeader>
            <SectionTitle><FaSearch /> Configurações de SEO</SectionTitle>
            <SaveButton onClick={() => saveSettings('seo')} disabled={saving}>
              {saving ? <FaSpinner className="spin" /> : <FaSave />}
              Salvar
            </SaveButton>
          </SectionHeader>

          <FormGrid>
            <FormGroup>
              <Label>Título padrão (meta title)</Label>
              <Input
                type="text"
                value={settings.seo_default_title || ''}
                onChange={(e) => updateSetting('seo_default_title', e.target.value)}
                placeholder="Vega Robotics - Tecnologia Industrial"
              />
            </FormGroup>

            <FormGroup>
              <Label>Imagem OG (para compartilhamento)</Label>
              <Input
                type="text"
                value={settings.seo_og_image || ''}
                onChange={(e) => updateSetting('seo_og_image', e.target.value)}
                placeholder="/og-image.jpg"
              />
            </FormGroup>
          </FormGrid>

          <FormGroup style={{ marginTop: '20px' }}>
            <Label>Descrição padrão (meta description)</Label>
            <TextArea
              value={settings.seo_default_description || ''}
              onChange={(e) => updateSetting('seo_default_description', e.target.value)}
              placeholder="Soluções em automação industrial, IA e visão computacional..."
            />
          </FormGroup>

          <FormGroup style={{ marginTop: '20px' }}>
            <Label>Palavras-chave (pressione Enter para adicionar)</Label>
            <TagsInput>
              {(settings.seo_keywords || []).map((keyword, index) => (
                <Tag key={index}>
                  {keyword}
                  <button type="button" onClick={() => removeKeyword(keyword)}>
                    <FaTimes />
                  </button>
                </Tag>
              ))}
              <TagInput
                type="text"
                value={newKeyword}
                onChange={(e) => setNewKeyword(e.target.value)}
                onKeyDown={addKeyword}
                placeholder="Adicionar palavra-chave..."
              />
            </TagsInput>
          </FormGroup>
        </Section>
      )}

      {/* ABA: ADMINS */}
      {activeTab === 'admins' && (
        <Section>
          <SectionHeader>
            <SectionTitle><FaUserShield /> Gerenciamento de Admins</SectionTitle>
          </SectionHeader>

          <InfoBox>
            <FaInfoCircle />
            <InfoText>
              Os usuários listados abaixo têm acesso ao painel administrativo.
              Apenas emails cadastrados aqui podem fazer login como administradores.
            </InfoText>
          </InfoBox>

          <AdminList>
            {admins.map((admin) => (
              <AdminItem key={admin.id}>
                <AdminInfo>
                  <AdminAvatar>{getInitials(admin.name, admin.email)}</AdminAvatar>
                  <AdminDetails>
                    <div className="name">{admin.name || 'Sem nome'}</div>
                    <div className="email">{admin.email}</div>
                  </AdminDetails>
                </AdminInfo>
                <DeleteButton onClick={() => removeAdmin(admin.id, admin.email)} title="Remover admin">
                  <FaTrash />
                </DeleteButton>
              </AdminItem>
            ))}
          </AdminList>

          <AddAdminForm onSubmit={addAdmin}>
            <AddInput
              type="text"
              placeholder="Nome (opcional)"
              value={newAdmin.name}
              onChange={(e) => setNewAdmin(prev => ({ ...prev, name: e.target.value }))}
            />
            <AddInput
              type="email"
              placeholder="Email *"
              value={newAdmin.email}
              onChange={(e) => setNewAdmin(prev => ({ ...prev, email: e.target.value }))}
              required
            />
            <AddButton type="submit" disabled={!newAdmin.email}>
              <FaPlus /> Adicionar
            </AddButton>
          </AddAdminForm>
        </Section>
      )}

      {/* ABA: LINKS ÚTEIS */}
      {activeTab === 'links' && (
        <Section>
          <SectionHeader>
            <SectionTitle><FaExternalLinkAlt /> Links Úteis</SectionTitle>
          </SectionHeader>

          <LinksGrid>
            {usefulLinks.map((link, index) => (
              <LinkCard key={index} href={link.url} target="_blank" rel="noopener noreferrer">
                <IconWrapper $bg={link.bg}>
                  <link.icon />
                </IconWrapper>
                <LinkContent>
                  <LinkTitle>
                    {link.title}
                    <FaExternalLinkAlt />
                  </LinkTitle>
                  <LinkDescription>{link.description}</LinkDescription>
                </LinkContent>
              </LinkCard>
            ))}
          </LinksGrid>
        </Section>
      )}
    </Container>
  );
};

export default Configuracoes;
